import { query, utils, models } from '@kurtosys/ksys-app-template';
import { IQueryOptions } from '../../../models/commonTypes';
import { computed, action, isArrayLike, observable } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { ILineChartConfiguration } from '../models';
import { ILineChartProps } from '@kurtosys/ksys-app-components/dist/components/charting/LineChart/models/ILineChartProps';
import { IDisclaimerProps } from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimer/models/IDisclaimerProps';
import { IGlobalConfiguration } from '@kurtosys/ksys-app-components/dist/models/IGlobalConfiguration';
import { TChartPeriods } from '@kurtosys/ksys-app-components/dist/components/charting/ChartPeriodSelection/models/TChartPeriods';
import { ILineProps } from '@kurtosys/ksys-app-components/dist/models/charting/ILineProps';
import { IPeriodTickOption } from '@kurtosys/ksys-app-components/dist/components/charting/ChartBase/models/IPeriodTickOption';
import { IAxisTickOptions } from '@kurtosys/ksys-app-components/dist/components/charting/ChartBase/models/IAxisTickOptions';
import { sortBy } from '@kurtosys/ksys-app-template/dist/utils/collection';

const isNullOrEmpty = utils.typeChecks.isNullOrEmpty;
const isNullOrUndefined = utils.typeChecks.isNullOrUndefined;

export class LineChartStore extends StoreBase {
	static componentKey: 'lineChart' = 'lineChart';

	@observable startDate: number | undefined;
	@observable endDate: number | undefined;
	@observable selectedPeriodKey: TChartPeriods | undefined;

	shouldCalculateSelectedPeriod: boolean = false;

	@computed
	get configuration(): ILineChartConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(LineChartStore.componentKey);
		}
		return undefined;
	}

	@computed
	get globalConfiguration(): IGlobalConfiguration | undefined {
		return this.storeContext.appStore && this.storeContext.appStore.globalConfiguration;
	}

	@action
	async initialize(): Promise<void> {
		if (!isNullOrEmpty(this.data)) {
			this.endDate = this.dataEndDate;
		}
	}

	@computed
	get disclaimers(): IDisclaimerProps[] {
		const { queryStore } = this.storeContext;
		const disclaimers = this.configuration && this.configuration.disclaimers;
		if (disclaimers) {
			return disclaimers.map((disclaimer) => {
				return query.Query.convertQueryKeyValue({ text: disclaimer }, queryStore.executionOptions);
			});
		}
		return [];
	}

	@computed
	get hasData() {
		return !utils.typeChecks.isNullOrUndefined(this.data) && this.data.length > 0;
	}

	@computed
	get data(): { dateAsDate: Date, dateAsTimestamp: number, [key: string]: any }[] {
		const dataQuery: IQueryOptions | undefined = this.configuration && this.configuration.data;
		const data = this.getQueryValue(dataQuery);
		if (Array.isArray(data)) {
			return data.map((item: any) => {
				const date = new Date(item.date);
				return {
					...item,
					dateAsDate: date,
					dateAsTimestamp: date.getTime(),
				};
			});
		}
		return [];
	}

	@computed
	get dataPeriodicity() {
		const dataQuery: IQueryOptions | undefined = this.configuration && this.configuration.data;
		if (dataQuery) {
			Object.assign(dataQuery, { rootKey: 'periodicity' });
			return this.getQueryValue(dataQuery);
		}
	}

	@computed
	get dataDates() {
		const dates: number[] = (this.data.slice() || []).map((data: any) => data.dateAsTimestamp);
		if (dates && dates.length > 0) {
			dates.sort((a, b) => a - b);
			return {
				startDate: dates[0],
				endDate: dates[dates.length - 1],
			};
		}
		return undefined;
	}

	@computed
	get dataStartDate() {
		return this.dataDates && this.dataDates.startDate;
	}

	@computed
	get dataEndDate() {
		return this.dataDates && this.dataDates.endDate;
	}

	@action
	selectChartPeriod(timestamp: number, selectedPeriodKey?: TChartPeriods) {
		this.startDate = timestamp;
		this.endDate = this.dataEndDate;
		this.selectedPeriodKey = selectedPeriodKey;
		this.shouldCalculateSelectedPeriod = false;
	}

	@action
	selectDateRange(startDate: string, endDate: string) {
		const startDateObj = this.data.find(d => d.date === startDate);
		const endDateObj = this.data.find(d => d.date === endDate);
		if (startDateObj && endDateObj) {
			this.selectedPeriodKey = undefined;
			this.startDate = startDateObj.dateAsTimestamp;
			this.endDate = endDateObj.dateAsTimestamp;
			this.shouldCalculateSelectedPeriod = true;
		}
	}

	@computed
	get periodData() {
		let data = this.data;
		const { startDateFallBackDirection } = this.storeContext.chartPeriodSelectionStore;

		if (!isNullOrUndefined(this.startDate) &&
			!isNullOrUndefined(this.endDate)) {
			let startDate = this.startDate;

			if (this.selectedPeriodKey === 'YTD') {
				const yearStartDate = new Date(new Date(this.endDate).getFullYear() + '-01-01').getTime();
				let previousYearsData = data.filter(x => x.dateAsTimestamp < yearStartDate);

				if (previousYearsData && previousYearsData.length > 0) {
					previousYearsData = previousYearsData.sort((a, b) => b.dateAsTimestamp - a.dateAsTimestamp);
					startDate = previousYearsData[0].dateAsTimestamp;
				}
			}

			if (startDateFallBackDirection === 'backwards') {
				const dataPointExists = data.find((data: any) => data.dateAsTimestamp === startDate);
				if (!dataPointExists) {
					const closestDate = sortBy(data.filter((data: any) => startDate >= data.dateAsTimestamp), data => data.dateAsTimestamp, 'DESC')[0];
					if (closestDate) {
						startDate = closestDate.dateAsTimestamp;
					}
				}
			}

			data = this.data.filter((data: any) => {
				return data.dateAsTimestamp >= (startDate || 0) &&
					data.dateAsTimestamp <= (this.endDate || 0);
			});
		}
		return data;
	}

	@computed
	get dynamicSeries(): ILineProps[] {
		const seriesQuery = this.configuration && this.configuration.seriesQuery;
		const dynamicSeries: ILineProps[] = seriesQuery ? this.getQueryValue(seriesQuery) : undefined;
		return dynamicSeries;
	}

	updateBaseXAxisTickOptions(chartProps: ILineChartProps): void {
		if (chartProps) {
			const DEFAULT_TICK_COUNT = 5;
			let xAxisTickOptions: IAxisTickOptions = { tickCount: DEFAULT_TICK_COUNT };

			// DEFAULT xAxisTickOptions to the chartPeriodSelectionStore
			const periodSelectionProps = this.storeContext.chartPeriodSelectionStore.configuration;
			if (periodSelectionProps) {
				let tickCount: number = DEFAULT_TICK_COUNT;
				let periodOptions: IPeriodTickOption[] = [];

				if (periodSelectionProps.defaultTickCount) {
					tickCount = periodSelectionProps.defaultTickCount;
				}

				if (periodSelectionProps.periods) {
					periodOptions = periodSelectionProps.periods.map((period) => {
						const periodTickCount = period.tickCount || tickCount;
						return {
							tickCount: periodTickCount,
							periodKey: (period.customKey ? `${ period.customKey.interval }${ period.customKey.period }` : period.key) || '',
						};
					});
				}

				xAxisTickOptions = {
					tickCount,
					periodOptions,
				};
			}

			// Apply overriding xAxisTickOptions configuration over the DEFAULT
			if (chartProps.dateOptions && chartProps.dateOptions.xAxisTickOptions) {
				const { tickCount: overrideTickCount, periodOptions: overridePeriodOptions } = chartProps.dateOptions.xAxisTickOptions;

				// Create a copy of overridePeriodOptions or initialize it as an empty array if undefined
				const overridePeriodOptionsCopy = overridePeriodOptions || [];
				const merged = utils.object.deepMergeObjectsWithOptions({ arrayMergeStrategy: 'DeepMergeByKeyOrAppend', getKeyFn: item => item.periodKey }, xAxisTickOptions, { periodOptions: overridePeriodOptionsCopy });

				xAxisTickOptions = {
					tickCount: overrideTickCount || xAxisTickOptions.tickCount,
					periodOptions: merged.periodOptions,
					...chartProps.dateOptions.xAxisTickOptions,
				};
			}

			// If no base dateOptions exist created it and append the default values
			if (!chartProps.dateOptions) {
				chartProps.dateOptions = {};
			}
			chartProps.dateOptions.xAxisTickOptions = xAxisTickOptions;
		}
	}

	@computed
	get lineChartProps(): ILineChartProps | undefined {
		const chartProps = this.configuration && this.configuration.chartProps;
		if (chartProps) {
			const dynamicSeries: ILineProps[] = this.dynamicSeries;
			if (!chartProps.seriesProps) {
				chartProps.seriesProps = dynamicSeries;
			}
			if (chartProps.additionalElements) {
				for (const element of chartProps.additionalElements) {
					if (element.queries) {
						const { props, queries } = element;
						element.props = this.mergeQueriesAndProps({ props, queries });
						delete element.queries;
					}
				}
			}
			const seriesProps = (chartProps && chartProps.seriesProps);
			if (seriesProps) {
				const seriesPropsOverrides = this.configuration && this.configuration.seriesPropsOverrides;
				if (isArrayLike(seriesPropsOverrides) && isArrayLike(seriesProps)) {
					const seriesPropsByDataKey = utils.collection.pivotCollection(seriesProps, seriesProp => !isNullOrUndefined(seriesProp.dataKey) ? seriesProp.dataKey.toString() : '');
					seriesPropsOverrides.forEach((seriesPropOverride) => {
						const seriesProp = seriesPropsByDataKey[seriesPropOverride.dataKey];
						if (seriesProp) {
							const name = this.getQueryValue(seriesPropOverride.name);
							seriesProp.toolTip = this.mergeQueriesAndProps({ props: seriesProp.toolTip, queries: seriesPropOverride.tooltip });
							seriesProp.name = name;
						}
					});
				}
				const rebaseOptions = this.getRebaseOptions(seriesProps, chartProps.rebaseOptions);
				this.updateBaseXAxisTickOptions(chartProps);
				const response: ILineChartProps = {
					...chartProps,
					rebaseOptions,
					selectedChartPeriod: this.selectedPeriodKey,
					data: this.periodData,
					disclaimersProps: {
						...chartProps.disclaimersProps,
						disclaimers: this.disclaimers,
					},
					summary: this.summary,
				};
				return response;
			}
		}
		return undefined;
	}

	@computed
	get isPeriodStartOlderOrEqual() {
		if (this.startDate && this.dataStartDate) {
			return this.startDate <= this.dataStartDate;
		}
		return false;
	}

	@computed
	get summary(): string | undefined {
		const summaryQuery = this.configuration && this.configuration.summaryQuery;
		return this.getQueryValue(summaryQuery);
	}

	getRebaseOptions(seriesProps: Partial<ILineProps>[], rebaseOptions?: models.data.IRebaseOptions): models.data.IRebaseOptions | undefined {
		const { queryStore } = this.storeContext;
		rebaseOptions = rebaseOptions || {
			enabled: false,
			inceptionInjection: {},
			series: [],
		};
		const rebaseOptionsQuery = this.configuration && this.configuration.rebaseOptionsQuery;
		let response: models.data.IRebaseOptions | undefined = queryStore.mergeQueriesAndProps({
			props: rebaseOptions,
			queries: rebaseOptionsQuery,
		});
		const override = this.configuration && this.configuration.rebaseOptions;
		if (override) {
			response = utils.object.deepMergeObjects(response, override);
		}
		if (response !== undefined && response.enabled && !response.series) {
			const rebaseSeriesBase = (this.configuration && this.configuration.rebaseSeriesBase) || {
				key: '',
				type: 'value',
			};
			response.series = seriesProps.map((series) => {
				return Object.assign({}, rebaseSeriesBase, {
					key: series.dataKey,
				});
			});
		}

		// Only use Inception Injection Configuration for periods that show the first record of data.
		if (response) {
			if (this.selectedPeriodKey !== 'ALL' && !this.isPeriodStartOlderOrEqual) {
				delete response.inceptionInjection;
			}
		}
		return response;
	}
}